/** 子中心路径识别与运行时辅助 */
import { SUB_CENTER, SUB_CENTER_VALUES, type SubCenterKey } from '@/constants/subCenter';
import { stripSubCenterFromPathname } from '@/utils/pathContext';
import { isValidClusterName } from '@/utils/tenantClusterStorage';

export { SUB_CENTER, type SubCenterKey } from '@/constants/subCenter';

export const SUB_CENTER_ROOT_TITLE: Record<SubCenterKey, string> = {
  [SUB_CENTER.COMPUTE]: 'computeCenter',
  [SUB_CENTER.RESOURCE]: 'resourceCenter',
  [SUB_CENTER.CHARGE]: 'chargeCenter',
  [SUB_CENTER.OPS]: 'operations',
  [SUB_CENTER.AI]: 'aiCenter',
  [SUB_CENTER.APP]: 'appCenter',
  [SUB_CENTER.BIZ]: 'operationsGovernance',
};

export const SUB_CENTER_ICON: Record<SubCenterKey, string> = {
  [SUB_CENTER.COMPUTE]: 'icon-suanli',
  [SUB_CENTER.RESOURCE]: 'icon-jiqunjiedian',
  [SUB_CENTER.CHARGE]: 'icon-jifeiguanli',
  [SUB_CENTER.OPS]: 'icon-yunweizhongxin3',
  [SUB_CENTER.AI]: 'icon-model',
  [SUB_CENTER.APP]: 'icon-ai-app',
  [SUB_CENTER.BIZ]: 'icon-yunyingguanli1',
};

/**
 * 租户模式下各子中心切换租户后的落地路径后缀（相对 `/console/:tenant/cluster/:cluster`）。
 * 模型中心进入 LLMGW 总览页。
 */
export const CONSOLE_SUB_CENTER_OVERVIEW_SUFFIX: Partial<Record<SubCenterKey, string>> = {
  [SUB_CENTER.COMPUTE]: '/overview',
  [SUB_CENTER.CHARGE]: `/${SUB_CENTER.CHARGE}/overview`,
  [SUB_CENTER.OPS]: `/${SUB_CENTER.OPS}/overview`,
  [SUB_CENTER.AI]: `/${SUB_CENTER.AI}/overview`,
  [SUB_CENTER.APP]: `/${SUB_CENTER.APP}/app-plaza`,
  [SUB_CENTER.BIZ]: `/${SUB_CENTER.BIZ}/charges`,
};

/** 组装租户控制台下当前子中心的总览（落地）页完整路径 */
export function getConsoleSubCenterOverviewPath(tenant: string, cluster: string, centerKey?: SubCenterKey | null): string {
  const effective = centerKey || SUB_CENTER.COMPUTE;
  const suffix = CONSOLE_SUB_CENTER_OVERVIEW_SUFFIX[effective] || '/overview';
  const clusterName = isValidClusterName(cluster) ? cluster : ':cluster';
  return `/console/${tenant}/cluster/${clusterName}${suffix}`;
}

export function getSubCenterFromPath(pathname: string): SubCenterKey | null {
  for (const segment of SUB_CENTER_VALUES) {
    if (pathname.includes(`/${segment}/`) || pathname.endsWith(`/${segment}`)) {
      return segment;
    }
  }
  return null;
}

/**
 * 当前生效的子中心：路径含子中心段时用路径；
 * 数据中心 / 租户控制台且无子中心段时视为算力中心。
 */
export function getEffectiveSubCenter(pathname: string): SubCenterKey | null {
  const fromPath = getSubCenterFromPath(pathname);
  if (fromPath) return fromPath;
  if (pathname.startsWith('/data-center/') || pathname.startsWith('/console/')) {
    return SUB_CENTER.COMPUTE;
  }
  return null;
}

/**
 * 将子中心路径段注入到数据中心 / 租户控制台路径中。
 * 算力中心无独立段；路径已含子中心段时不重复注入。
 * 例：`/data-center/dc1/users` + ops-center → `/data-center/dc1/ops-center/users`
 */
export function injectSubCenterIntoPath(pathname: string, centerKey: SubCenterKey | null | undefined): string {
  if (!pathname || !centerKey || centerKey === SUB_CENTER.COMPUTE) return pathname;
  if (getSubCenterFromPath(pathname)) return pathname;

  const consoleMatch = pathname.match(/^(\/console\/[^/]+\/cluster\/[^/]+)(\/.*)?$/);
  if (consoleMatch) {
    return `${consoleMatch[1]}/${centerKey}${consoleMatch[2] || ''}`;
  }

  const dcMatch = pathname.match(/^(\/data-center\/[^/]+)(\/.*)?$/);
  if (dcMatch) {
    return `${dcMatch[1]}/${centerKey}${dcMatch[2] || ''}`;
  }

  return pathname;
}

/** 递归取菜单树中第一个叶子 path（深度优先，顺序与静态菜单定义一致） */
export function findFirstMenuPath(items: any[] | undefined): string | undefined {
  if (!items?.length) return undefined;
  for (const item of items) {
    if (item.path) return item.path;
    const childPath = findFirstMenuPath(item.children);
    if (childPath) return childPath;
  }
  return undefined;
}

/** 打开外链子中心：优先默认入口页，否则首个有权限的页面 */
export function buildExternalCenterUrlFromMenu(item: { centerKey?: SubCenterKey; children?: any[] }) {
  return findFirstMenuPath(item.children) || '';
}

/** 租户 filterAuthMenu 结果根节点上取首个可访问页面路径；无权限菜单时返回 undefined */
export function getFirstAccessibleConsolePath(filteredTenantMenuRoot: { children?: any[] } | null | undefined): string | undefined {
  return findFirstMenuPath(filteredTenantMenuRoot?.children);
}

/** 子中心左侧菜单树 */
export function getSubCenterMenu(centerKey: SubCenterKey, modules: any[]) {
  return [
    {
      title: SUB_CENTER_ROOT_TITLE[centerKey],
      icon: SUB_CENTER_ICON[centerKey],
      children: modules,
    },
  ];
}

/**
 * 组装子中心侧栏：上部为当前中心业务菜单，底部为 fixBottom 项且排除当前子中心。
 */
export function buildSubCenterLeftMenu(mainMenu: any, effectiveCenter: SubCenterKey) {
  const centerItem = mainMenu?.children?.find((item) => item.externalLink && item.centerKey === effectiveCenter);
  if (!centerItem?.children?.length) return null;

  let centerChildren = centerItem.children as any[];

  // 算力中心：若存在 5 级菜单，将该模块的第 3 级提升到第 1 级
  if (effectiveCenter === SUB_CENTER.COMPUTE) {
    centerChildren = flattenComputeCenterChildren(centerChildren);
  }

  // 非外链 fixBottom（成员/角色/审计等）注入当前子中心路径段，避免跳转后落到算力中心
  const bottomItems = (mainMenu.children || [])
    .filter(
      (item) =>
        item.fixBottom && item.centerKey !== effectiveCenter && (!item.onlyCenters?.length || item.onlyCenters.includes(effectiveCenter)),
    )
    .map((item) => {
      if (item.externalLink || !item.path) return item;
      return {
        ...item,
        path: injectSubCenterIntoPath(item.path, effectiveCenter),
      };
    });

  const root = getSubCenterMenu(effectiveCenter, centerChildren)?.[0] || {};
  return {
    ...root,
    children: [...centerChildren, ...bottomItems],
  };
}

/** 数据中心算力中心：有 5 级菜单时跳过第 2 级展平 */
function flattenComputeCenterChildren(modules: any[]): any[] {
  const newChildren: any[] = [];
  modules.forEach((module) => {
    if (!module?.children) {
      newChildren.push(module);
      return;
    }
    const hasLevel5 = module.children.some((child) => child?.children?.some((grandchild) => grandchild?.children?.length > 0));
    if (hasLevel5) {
      newChildren.push(...module.children);
    } else {
      newChildren.push(module);
    }
  });
  return newChildren;
}

export { stripSubCenterFromPathname };
